import { Haptics, NotificationType } from '@capacitor/haptics'
import { v4 as getUUID } from 'uuid'
import { ref } from 'vue'
import { removeMarkdown } from '~/utils/remove-markdown'

const NOTIFICATION_VISIBILITY_DURATION = 8000
const TITLE_CHANGE_DURATION = 1500
const DEFAULT_DOCUMENT_TITLE = 'WN Hub'
const SOUND_PATH = '/sound/notification.mp3'

export interface NotificationInterface {
  title: string
  description?: string
  type?: string
  icon?: string
  theme?: string
  playSound?: boolean
  changeTitle?: boolean
  autoHide?: boolean
  clickHandler?: () => void
}

export class Notification implements NotificationInterface {
  title!: string
  description!: string
  type!: string
  icon = 'outline_check_circle'
  theme = 'success'
  playSound!: boolean
  changeTitle!: boolean
  clickHandler?: () => void
  autoHide = true
  uuid = getUUID()

  constructor(params: NotificationInterface) {
    params.title && (this.title = removeMarkdown(params.title))
    params.description &&
      (this.description = removeMarkdown(params.description))
    params.icon && (this.icon = params.icon)
    params.theme && (this.theme = params.theme)
    params.type && (this.type = params.type)
    params.playSound && (this.playSound = params.playSound)
    params.changeTitle && (this.changeTitle = params.changeTitle)
    params.autoHide !== undefined && (this.autoHide = params.autoHide)
    params.clickHandler && (this.clickHandler = params.clickHandler)
  }
}

const state = ref([] as Notification[])

let intervalId: number

function pushNotification(params: NotificationInterface) {
  const { isApp } = useDetect()
  const notification = new Notification(params)

  state.value.push(notification)

  if (notification.playSound) {
    if (isApp) {
      Haptics.notification({ type: NotificationType.Warning })
    } else {
      const sound = new Audio(SOUND_PATH)

      sound.autoplay = true
      sound.muted = false

      sound.oncanplaythrough = () => {
        sound.play()
      }
    }
  }

  if (notification.changeTitle) {
    if (isApp) {
      return
    }
    if (intervalId) {
      clearInterval(intervalId)
    }

    intervalId = setTitleChangeInterval(notification.title)
  }

  if (notification.autoHide) {
    setTimeout(() => {
      if (state.value.length > 0) {
        const idx = state.value.findIndex(
          (item) => item.uuid === notification.uuid
        )
        idx !== undefined && state.value.splice(idx, 1)
      }
      if (!state.value.length && intervalId) {
        clearInterval(intervalId)
        document.title = DEFAULT_DOCUMENT_TITLE
      }
    }, NOTIFICATION_VISIBILITY_DURATION)
  }
}

function removeNotification(notification: Notification) {
  const i = state.value.indexOf(notification)

  if (i === state.value.length - 1) {
    if (intervalId) {
      clearInterval(intervalId)
      document.title = DEFAULT_DOCUMENT_TITLE
    }
    const newLast = state.value[i - 1]

    if (newLast?.changeTitle) {
      intervalId = setTitleChangeInterval(newLast.title)
    }
  }
  state.value.splice(i, 1)
}

function setTitleChangeInterval(title: string) {
  let showTitle = true

  return setInterval(
    (function func() {
      document.title = showTitle ? title : DEFAULT_DOCUMENT_TITLE
      showTitle = !showTitle
      return func
    })(),
    TITLE_CHANGE_DURATION
  ) as unknown as number
}

export function useNotifications() {
  return { list: state, pushNotification, removeNotification }
}
